import React from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { Card, RadioButton } from 'react-native-paper';
import { styles } from './ProfileViewStyle';
import { FormCard } from './FormCard';
import { LabeledField } from '../components/LabeledField';
import { TimezoneSelector } from '../components/TimezoneSelector';
import usePasswordToggle from '../components/usePasswordToggle';
import { calculateYearsAgo, dateStringToDate } from '../utils/date';
import { theme } from '../theme';
import { formatBirthDateInput } from '../utils/formatBirthDateInput';
import { MIN_TAP_TARGET, useIsNarrowForm } from './ResponsiveFormContext';
export const ContactInfoCard = ({ values, creating, editing, setFieldValue, errors }) => {
    const [showPassword, PasswordToggleIcon] = usePasswordToggle(values.password.length !== 0 || values.confirmPassword.length !== 0, theme.colors.textLinkStrong, theme.colors.controlInactive);
    const readOnly = !editing && !creating;
    // Phone-sized public sign-up only: stack the side-by-side rows.
    const narrow = useIsNarrowForm();
    const fieldRow = narrow ? localStyles.stackedRow : styles.row;
    const genderOptions = [
        { value: 'M', label: 'Male' },
        { value: 'F', label: 'Female' },
    ];
    return (<FormCard>
      <Card.Content>
        <View style={fieldRow}>
          <LabeledField required name="fullName" label="Full name" placeholder="First name and last name" width={400} readOnly={readOnly} signupInput={{
            autoComplete: 'name',
            textContentType: 'name',
            autoCapitalize: 'words',
            autoCorrect: false,
        }}/>
          <LabeledField required name="birthDate" label="Date of birth" placeholder="MM-DD-YYYY" width={140} keyboardType="string" readOnly={readOnly} signupInput={{
            keyboardType: 'number-pad',
            autoComplete: 'bday',
            formatText: formatBirthDateInput,
        }}/>

          {values.birthDate ? (<Text style={narrow ? localStyles.ageStacked : localStyles.age}>{`Age: ${dateStringToDate(values.birthDate)
                ? calculateYearsAgo(dateStringToDate(values.birthDate), new Date())
                : ''}`}</Text>) : null}
        </View>

        <View style={[styles.row]}>
          <RadioButton.Group onValueChange={value => setFieldValue('gender', value)} value={values.gender}>
            <View style={[
            styles.row,
            readOnly ? styles.pointerEventsNone : styles.pointerEventsAuto,
        ]}>
              {genderOptions.map(option => narrow ? (
        // Phone sign-up: the whole option is a 44pt tap target.
        <Pressable key={option.value} accessibilityRole="radio" accessibilityState={{
                checked: values.gender === option.value,
            }} onPress={() => setFieldValue('gender', option.value)} style={localStyles.radioOption}>
                    <RadioButton value={option.value}/>
                    <Text style={localStyles.radioOptionText}>
                      {option.label}
                    </Text>
                  </Pressable>) : (<View key={option.value} style={styles.row}>
                    <RadioButton value={option.value}/>
                    <Text style={styles.radioItemText}>{option.label}</Text>
                  </View>))}
            </View>
          </RadioButton.Group>
        </View>
        {errors.gender ? (<Text style={styles.errorText}>{errors.gender}</Text>) : null}

        <View style={fieldRow}>
          <LabeledField required name="email" label="Email address" placeholder="you@somewhere.com" width={250} keyboardType="email-address" readOnly={readOnly} signupInput={{
            autoComplete: 'email',
            textContentType: 'emailAddress',
            autoCapitalize: 'none',
            autoCorrect: false,
        }}/>
          <LabeledField name="phoneNumber" label="Phone number" placeholder="(xxx) xxx-xxxx" width={150} keyboardType="numeric" readOnly={readOnly} required signupInput={{
            keyboardType: 'phone-pad',
            autoComplete: 'tel',
            textContentType: 'telephoneNumber',
        }}/>
          <TimezoneSelector values={values} readOnly={readOnly} setFieldValue={setFieldValue}/>
        </View>

        {creating && (<View style={fieldRow}>
            <LabeledField required name="password" label="Password" placeholder="Minimum of 8 characters" secureTextEntry={!showPassword} width={200} readOnly={readOnly} signupInput={{
                autoComplete: 'new-password',
                textContentType: 'newPassword',
                autoCapitalize: 'none',
                autoCorrect: false,
            }}/>
            <LabeledField required name="confirmPassword" label="Confirm password" placeholder="Minimum of 8 characters" secureTextEntry={!showPassword} width={200} readOnly={readOnly} signupInput={{
                autoComplete: 'new-password',
                textContentType: 'newPassword',
                autoCapitalize: 'none',
                autoCorrect: false,
            }}/>
            <View style={narrow
                ? localStyles.passwordToggleIconStacked
                : localStyles.passwordToggleIcon}>
              <PasswordToggleIcon />
            </View>
          </View>)}
      </Card.Content>
    </FormCard>);
};
const localStyles = StyleSheet.create({
    age: {
        marginTop: 44,
        marginLeft: 4,
        fontStyle: 'italic',
    },
    passwordToggleIcon: {
        marginTop: 40,
    },
    // Narrow (phone) public sign-up only, below.
    stackedRow: {
        flexDirection: 'column',
    },
    ageStacked: {
        marginLeft: 10,
        fontStyle: 'italic',
    },
    passwordToggleIconStacked: {
        marginLeft: 10,
        alignSelf: 'flex-start',
    },
    radioOption: {
        flexDirection: 'row',
        alignItems: 'center',
        minHeight: MIN_TAP_TARGET,
        marginRight: 16,
    },
    radioOptionText: {
        fontSize: 16,
    },
});
